export type ActivityRecord = {
  id: string;
  startedAt: string;
  durationSeconds: number;
  outcome: "completed" | "stopped";
};

export type ActivitySummary = {
  sessions7d: number;
  minutes7d: number;
  activeDays7d: number;
  averageMinutes: number;
  suggestedTime: string;
  suggestedWeeklyTarget: number;
};

export type ReminderSchedule = {
  time: string;
  weeklyTarget: number;
  timeZone: string;
};

const WEEKDAY_GROUPS: Record<number, number[]> = {
  1: [1],
  2: [1, 4],
  3: [1, 3, 5],
  4: [1, 2, 4, 5],
  5: [1, 2, 3, 4, 5],
  6: [1, 2, 3, 4, 5, 6],
  7: [0, 1, 2, 3, 4, 5, 6],
};

export function activitySummary(records: ActivityRecord[], now = new Date()): ActivitySummary {
  const since = now.getTime() - 7 * 24 * 60 * 60 * 1_000;
  const recent = records.filter((record) => {
    const startedAt = Date.parse(record.startedAt);
    return Number.isFinite(startedAt) && startedAt >= since && startedAt <= now.getTime();
  });
  const completed = recent.filter((record) => record.outcome === "completed");
  const totalSeconds = recent.reduce((sum, record) => sum + Math.max(0, record.durationSeconds), 0);
  const activeDays = new Set(recent.map((record) => {
    const date = new Date(record.startedAt);
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
  })).size;
  const hourCounts = completed.reduce<Record<number, number>>((counts, record) => {
    const hour = new Date(record.startedAt).getHours();
    counts[hour] = (counts[hour] || 0) + 1;
    return counts;
  }, {});
  const preferredHour = Object.entries(hourCounts).sort((a, b) => Number(b[1]) - Number(a[1]) || Number(a[0]) - Number(b[0]))[0]?.[0];
  const suggestedHour = preferredHour === undefined ? 15 : Math.min(20, Math.max(8, Number(preferredHour)));
  const suggestedTarget = completed.length === 0 ? 3 : Math.min(7, Math.max(2, completed.length));
  return {
    sessions7d: recent.length,
    minutes7d: Math.round(totalSeconds / 60),
    activeDays7d: activeDays,
    averageMinutes: recent.length ? Math.max(1, Math.round(totalSeconds / recent.length / 60)) : 0,
    suggestedTime: `${String(suggestedHour).padStart(2, "0")}:00`,
    suggestedWeeklyTarget: suggestedTarget,
  };
}

function localParts(now: Date, timeZone: string) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    weekday: "short",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);
  const value = (type: Intl.DateTimeFormatPartTypes) => parts.find((part) => part.type === type)?.value || "";
  const weekdays: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  return {
    date: `${value("year")}-${value("month")}-${value("day")}`,
    weekday: weekdays[value("weekday")],
    minutes: Number(value("hour")) * 60 + Number(value("minute")),
  };
}

export function reminderDue(
  schedule: ReminderSchedule,
  lastSentLocalDate: string | null,
  now = new Date(),
) {
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(schedule.time)) return null;
  const weekdays = WEEKDAY_GROUPS[Math.min(7, Math.max(1, Math.round(schedule.weeklyTarget)))];
  if (!weekdays) return null;
  let local;
  try {
    local = localParts(now, schedule.timeZone);
  } catch {
    return null;
  }
  const [hour, minute] = schedule.time.split(":").map(Number);
  const scheduledMinutes = hour * 60 + minute;
  if (!weekdays.includes(local.weekday) || local.date === lastSentLocalDate) return null;
  return local.minutes >= scheduledMinutes && local.minutes < scheduledMinutes + 10 ? local.date : null;
}
